import { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import FolderNamingDialog from 'domains/core/components/folders/FolderNamingDialog';
import { ContentType } from 'domains/content/types';
import { URLPaths } from 'models/enums';
import useContentQueryManager from 'hooks/useContentQueryParams';
import useContentWithFolders from 'hooks/queries/useContentWithFolders';
import useArchivedContentList from 'hooks/queries/useArchivedContentList';
import useFeatureSettings from 'hooks/queries/useFeatureSettings';
import useCreateContentFolder from 'hooks/mutations/useCreateContentFolder';
import ContentPageLayout from './ContentPageLayout';
import { contentTypeToLabel } from './ContentPageLayout/ContentPageLayout';
import useGetCreateContentDropdownOptions from './useGetCreateContentDropdownOptions';
import { isRootFolder } from 'utils/folders';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useAccessCentrePermissions } from 'hooks/useAccessCentrePermissions';

const ContentPageWithFolders = () => {
    const { data: featureSettings, isSuccess: isFeatureSettingsSuccess } = useFeatureSettings();
    const hasDirectMailContent = isFeatureSettingsSuccess && featureSettings?.directmail_enabled;
    const hasVoiceContent = isFeatureSettingsSuccess && featureSettings.voice_enabled;
    const { whatsappContent: hasWhatsappContent } = useFlags();
    const { canManageContent } = useAccessCentrePermissions();

    const [isCreateFolderModalVisible, setIsCreateFolderModalVisible] = useState(false);
    const [isArchivedView, setIsArchivedView] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    const history = useHistory();
    const location = useLocation();
    const contentQueryManager = useContentQueryManager();
    const { changeViewParams, type, folderId, statuses } = contentQueryManager;

    // Only call the appropriate query based on current view
    const contentQuery = useContentWithFolders({
        folderId,
        type,
        searchTerm,
        statuses,
        reactQueryOptions: { enabled: !isArchivedView },
    });
    const archivedContentQuery = useArchivedContentList({
        type,
        searchTerm,
        statuses,
        reactQueryOptions: { enabled: isArchivedView },
    });

    // Check if we're on the archive URL and set state accordingly
    useEffect(() => {
        const isArchiveUrl = location.pathname === URLPaths.CONTENT_ARCHIVED;
        setIsArchivedView(isArchiveUrl);
    }, [location.pathname]);

    const { refetch: refetchContent } = contentQuery;

    const isUserOnRootLevel = isRootFolder(folderId);

    const createContentDropdownOptions = [
        ...useGetCreateContentDropdownOptions(),
        ...(isUserOnRootLevel
            ? [
                  {
                      label: 'folder',
                      onClick: () => setIsCreateFolderModalVisible(true),
                  },
              ]
            : []),
    ];

    const createFolder = (folderName: string) => {
        const body = {
            name: folderName,
        };
        saveFolder(body);
    };

    const onCreateFolderSuccess = (response: { id: number }) => {
        changeViewParams({ newFolderId: response.id });
        refetchContent();
        setIsCreateFolderModalVisible(false);
    };

    const { mutate: saveFolder, isLoading: isFolderSaving } = useCreateContentFolder({
        onSuccess: onCreateFolderSuccess,
    });

    const handleToggleArchivedView = () => {
        const newArchivedView = !isArchivedView;
        setIsArchivedView(newArchivedView);

        // Update URL based on archived view state, preserving status filter
        if (newArchivedView) {
            const params = new URLSearchParams();
            if (type && type !== ContentType.ALL) {
                params.set('type', type);
            }
            if (statuses.length > 0) {
                params.set('status', statuses.join(','));
            }
            const queryString = params.toString();
            history.push(`${URLPaths.CONTENT_ARCHIVED}${queryString ? `?${queryString}` : ''}`);
        } else {
            // Use changeViewParams to properly handle folder paths (preserves status)
            changeViewParams({ newFolderId: folderId, newType: type });
        }
    };

    const currentContentQuery = isArchivedView ? archivedContentQuery : contentQuery;

    const handleTypeChange = (newType: ContentType) => {
        if (isArchivedView) {
            // If we're in archived view, navigate to archived URL with type parameter
            // Preserve status filter if present
            const params = new URLSearchParams();
            if (newType !== ContentType.ALL) {
                params.set('type', newType);
            }
            if (statuses.length > 0) {
                params.set('status', statuses.join(','));
            }
            const queryString = params.toString();
            history.push(`${URLPaths.CONTENT_ARCHIVED}${queryString ? `?${queryString}` : ''}`);
        } else {
            // If we're in regular view, use the normal changeViewParams which preserves status
            changeViewParams({ newType });
        }
    };

    const headerOtherDropdownProps = {
        label: contentTypeToLabel(type),
        options: [
            {
                label: contentTypeToLabel(ContentType.ALL),
                onClick: () => handleTypeChange(ContentType.ALL),
                selected: type === ContentType.ALL || !type,
            },
            // Don't show Folder option in archived view
            ...(isArchivedView
                ? []
                : [
                      {
                          label: contentTypeToLabel(ContentType.FOLDER),
                          onClick: () => handleTypeChange(ContentType.FOLDER),
                          selected: type === ContentType.FOLDER,
                      },
                  ]),
            {
                label: contentTypeToLabel(ContentType.EMAIL),
                onClick: () => handleTypeChange(ContentType.EMAIL),
                selected: type === ContentType.EMAIL,
            },
            {
                label: contentTypeToLabel(ContentType.SMS),
                onClick: () => handleTypeChange(ContentType.SMS),
                selected: type === ContentType.SMS,
            },
            ...(hasDirectMailContent
                ? [
                      {
                          label: contentTypeToLabel(ContentType.DIRECT_MAIL),
                          onClick: () => handleTypeChange(ContentType.DIRECT_MAIL),
                          selected: type === ContentType.DIRECT_MAIL,
                      },
                  ]
                : []),
            {
                dataTestId: 'view-landing-pages',
                label: contentTypeToLabel(ContentType.PAGE),
                onClick: () => handleTypeChange(ContentType.PAGE),
                selected: type === ContentType.PAGE,
            },
            ...(hasVoiceContent
                ? [
                      {
                          label: contentTypeToLabel(ContentType.VOICE),
                          onClick: () => handleTypeChange(ContentType.VOICE),
                          selected: type === ContentType.VOICE,
                      },
                  ]
                : []),
            ...(hasWhatsappContent
                ? [
                      {
                          label: contentTypeToLabel(ContentType.WHATSAPP),
                          onClick: () => handleTypeChange(ContentType.WHATSAPP),
                          selected: type === ContentType.WHATSAPP,
                      },
                  ]
                : []),
        ],
    };

    return (
        <>
            <FolderNamingDialog
                data-testid="folder-name-input"
                disableActionButton={isFolderSaving}
                initialValue="Untitled Folder"
                isDialogVisible={isCreateFolderModalVisible}
                onClose={() => setIsCreateFolderModalVisible(false)}
                onSubmit={createFolder}
                title="create new folder"
            />
            <ContentPageLayout
                contentQuery={currentContentQuery}
                createContentDropdownOptions={createContentDropdownOptions}
                headerOtherDropdownProps={headerOtherDropdownProps}
                isNestedContent={!isUserOnRootLevel}
                isArchivedView={isArchivedView}
                onToggleArchivedView={handleToggleArchivedView}
                searchTerm={searchTerm}
                onSearchTermChange={setSearchTerm}
                selectedStatuses={statuses}
                onStatusFilterApply={changeViewParams}
                canManageContent={canManageContent}
            />
        </>
    );
};

export default ContentPageWithFolders;
